//  Map Template
//	web services - Aaron Zygmunt: azygmunt2@cps.edu
//  06/2025
//  04/2026 - global ui updates

// Naming conventions used in this code:
// variables that begin with $ contain jquery elements or selectors
// functions that begin with $ return a single jquery element
// iterators used: m for markers, p for properties, f for filters

import {setOptions, importLibrary} from "@googlemaps/js-api-loader";
import $ from "jquery";
import "jquery-ui/dist/jquery-ui.min";
import DataTable from "datatables.net-dt";
import markerSvg from "../../../global/images/marker_pin.svg";
import translations from "../../../global/translations.json";
import {
    $addressDirectionLink,
    $resultTable,
    $targets,
    animateMarker,
    chicago,
    chicagoBounds,
    color,
    geocodeRequest,
    getAPIValues,
    googleAPIKey,
    initModal,
    inputSubstringMatch,
    menuHighlight,
    parseTranslations,
    positionMarkersOnMap,
    scrollToRow,
    setAddressMarker,
    setMapBounds,
    showPolygon,
    POLYGON_STYLE,
    stopAnimateMarker,
    textContrast,
    toSnakeCase,
    translatePage,
    updateCMSElements
} from "../../../global/map-library";
import "../../../global/map-library.scss";
import "../scss/styles.scss";

$targets.year = $(".current-year");
$targets.vacancies = $("#vacancies-menu");
$targets.lscType = $("#lsc-type-menu");
$targets.seatType = $("#seat-menu");

// Get parameters from URL and default to "en" if not passed
const urlParams = new URLSearchParams(window.location.search);
let lang = "en";
if (urlParams.get("language")) {
    lang = urlParams.get("language").toLowerCase();
} else if (urlParams.get("lang")) {
    lang = urlParams.get("lang").toLowerCase();
}

// "fullscreen" parameter determines if page loads with the overlay opened or closed
// currently passing it any value is treated as true and loads with the overlay closed 
const fullscreen = urlParams.get("fullscreen");

// add selection highlighting to jquery ui autocomplete menu
$.ui.autocomplete.prototype._renderItem = menuHighlight;

setOptions({
    key: googleAPIKey("AIzaSyANShVxNx4r5mHjyMG1tf83AbDYJq09Irw"), // "Browser Key - Maps" google api key - should be unique for every project
    v: "weekly"
});

try {
    ///////////////////////////////////// GOOGLE MAP LIBRARIES /////////////////////////////////////

    const {Map, InfoWindow, Polygon} = await importLibrary("maps");
    const {AdvancedMarkerElement, PinElement} = await importLibrary("marker");
    const {AutocompleteSessionToken, AutocompleteSuggestion} = await importLibrary("places");
    await importLibrary("geometry");

    ///////////////////////////////////// GLOBALS /////////////////////////////////////

    const geoAPI = "/api/maps/GeoJSON";
    // const geoAPI = "https://api.cps.edu/maps/cps/GeoJSON";
    // const VACANCIES_API = "https://api.cps.edu/lsc/cps/vacanciesbyterm";
    const VACANCIES_API = "/api/lsc/vacancies";
    // const MEETINGS_API = "https://api.cps.edu/LSC/CPS/GetMeetingsByOrigin";
    // const MEETINGS_API = "/Static/dist/apps/LSCMembers/assets/meetings.json";
    const MEETINGS_API = "/api/lsc/meetings";
    // const MEMBERS_API = "https://api.cps.edu/LSC/CPS/Members";
    const MEMBERS_API = "/api/lsc/members";
    const schoolAPIParameters = {"mapName": "SCHOOL"};
    const LSCAPIParameters = {"mapName": "LSC", "year": 2022};
    const membersApiParameters = {"year": "current"};
    const vacanciesApiParameters = {"term": "current"};

    const markers = []; // hold the pins currently displayed on the map
    let filteredData;  // global array to hold the data to be displayed. it will be filled with api data reduced by filtering
    const infoWindow = new InfoWindow();
    let addressMarker;
    let loaded = false; // used to determine initial map load and prevent looping
    let dataTable; // used for datatables.js initialization
    let location // holds the google map location after an address search 
    let LSCSearchData // holds the api data after an address search 
    const labelMarkers = [];
    let currentMarkerFilter = ""; // which marker filter is currently active
    let currentMarker = null; // marker for the currently open infowindow. Only used to remember state for translation

    /*************************************************************************************************************/

    // pull all api data
    const [vacancyData, boundaryData, schoolData, meetingData, memberData] = await Promise.all([
        getAPIValues(VACANCIES_API, vacanciesApiParameters),
        getAPIValues(geoAPI, LSCAPIParameters),
        getAPIValues(geoAPI, schoolAPIParameters),
        getAPIValues(MEETINGS_API),
        getAPIValues(MEMBERS_API, membersApiParameters)
    ]);

    // console.log([vacancyData, boundaryData, schoolData, meetingData, memberData]);

    // need to pull icons only for valid LSC results - schools with vacancy data
    // const dataAll = schoolData.features.filter(s => vacancyData.find(v => v.SchoolId.toString() === s.properties.School_ID) !== undefined);
    const dataAll = [];
    schoolData.features.forEach(s => {
        const vacancies = vacancyData.find(v => v.SchoolId.toString() === s.properties.School_ID);
        if (vacancies) {
            // FOR TESTING ONLY. make ~2.5% inactive for testing
            /*
                        if (mulberry32(vacancies.SchoolId) < 0.025) {
                            vacancies.LSCType = "Inactive";
                        }
            */
            s.vacancies = vacancies;

            const boundary = boundaryData.features.find(b => b.properties.SCHOOL_ID === s.properties.School_ID);
            if (boundary) {
                s.boundary = boundary;
            }

            dataAll.push(s);
        }
    });
    dataAll.sort((a, b) => a.properties.Name.localeCompare(b.properties.Name));

    // Update the page with contents from the CMS
    updateCMSElements($("h1", $targets.overlay), window.appConfig.pageHeading, $($targets.bodyText), window.appConfig.pageContent);

    // create the LSC Type menu options from the vacancy data
    buildMenu();

    // gather the translations from the page
    const pageTranslations = parseTranslations($targets.overlay);

    // append page translations to translations object
    Object.keys(pageTranslations).forEach(l => {
        Object.keys(pageTranslations[l]).forEach(t => {
            translations[l][t] = pageTranslations[l][t];
        });
    });

    // use the translations object data and selected language to replace text elements on the page
    translatePage(lang, $targets);

    // create main map object
    const map = new Map($targets.map[0], {
        mapId: "ab574e903403777fbeab6b49",
        center: chicago,
        streetViewControl: false,
        fullscreenControl: false,
        mapTypeControl: true,
        mapTypeControlOptions: {
            style: google.maps.MapTypeControlStyle.HORIZONTAL_BAR,
            position: google.maps.ControlPosition.TOP_RIGHT
        }
    });

    // show UI
    if (fullscreen) {
        $targets.mainWrapper.toggle(false);
        $targets.displayToggle.attr("aria-expanded", (index, attr) => (attr === "true" ? "false" : "true"));
    }
    $targets.dataLoader.remove();
    $targets.overlay.removeAttr("hidden");

    updateFilters(dataAll);

    // Create a session token for autocomplete suggestions
    const token = new AutocompleteSessionToken();

    // initialize the autocomplete menu
    $targets.input.autocomplete({
        // use google autocomplete as a dynamic data source
        source: async function (request, response) {
            // create the list of addresses with google's Places(new) autocomplete with a shortened address as the label and "address" as the value
            let addressList = [];
            if (request.term.length > 1) {
                const autocompleteRequest = {
                    input: request.term,
                    locationRestriction: chicagoBounds,
                    origin: chicago,
                    includedPrimaryTypes: ["street_address"],
                    language: "en-US",
                    region: "us",
                    sessionToken: token
                };
                const {suggestions} = await AutocompleteSuggestion.fetchAutocompleteSuggestions(autocompleteRequest);
                for (const suggestion of suggestions) {
                    // only add addresses that end with "Chicago, IL"
                    if (suggestion.placePrediction.text.toString().endsWith("Chicago, IL")) {
                        addressList.push({
                            label: suggestion.placePrediction.mainText.toString(),
                            value: "address"
                        });
                    }
                }
            }

            // create the schools list in label/value format
            const schoolList = filteredData.filter((s) => s.properties.Name.toUpperCase().includes(request.term.toUpperCase())).map(s => ({
                label: s.properties.Name,
                value: "school"
            }));

            // return the list for the autocomplete menu as the response
            response([...schoolList, ...addressList]);
        },
        focus: function (event, ui) { // autocomplete result is focused on
            $targets.input.val(ui.item.label);
            return false;
        },
        select: (e, ui) => {
            e.preventDefault();
            $targets.input.val(ui.item.label);
            $targets.input.autocomplete("close");
            if (ui.item.value === "address") {
                geocodeRequest(ui.item.label, googleGeoCallback);
            } else {
                // this is a school. Select the school with a string match on its label
                selectSchool(markers.find(m => m.properties.Name === ui.item.label));
            }
        }
    });

    /********************* EVENTS *********************/

    // pan the map into the proper position after everything is drawn
    map.addListener("tilesloaded", () => {
        if (!loaded) {
            setTimeout(() => {
                // fires more reliably when wrapped in an asynchronous call. Using a delay 0 in this case
                positionMarkersOnMap(map, $targets);
                loaded = true;
            }, 0);
        }
    });

    // resize the labels after the map is done with any updates
    google.maps.event.addListener(map, "idle", function () {
        $(".map-label").css({"font-size": map.getZoom() >= 14 ? "24px" : "14px"});
    });

    // remove the table row highlight and clear the input field when the infowindow is closed
    infoWindow.addListener("close", () => {
        $targets.input.val("");
        $("tr.selected-row").removeClass("selected-row");
        // map.data.forEach(feature => map.data.remove(feature));
    });

    // mixed search button clicked
    $targets.search.on("click", () => {
        mixedSearch($targets.input.val());
    });

    // input field enter key
    $targets.input.on("keydown", async (e) => {
        if (e.key === "Enter") { // Check if the Enter key is pressed
            if ($targets.input.autocomplete) {
                $targets.input.autocomplete("close");
            }
            mixedSearch($targets.input.val());
        }
    });

    // FILTER EVENT LOGIC:
    // Set the UI state first, then filter the data by examining the UI and applying the filters
    // This way, the data shown and current state of the UI should always be in sync
    $("input.filter-checkbox", $targets.filters).on("input", function () {
        // if there is LSC Data from an address search, then filter the LSC Data. otherwise filter dataAll
        updateFilters(LSCSearchData ? LSCSearchData : dataAll);
    });

    $(".filter-menu", $targets.filters).on("input", function () {
        updateFilters(LSCSearchData ? LSCSearchData : dataAll);
    });

    $targets.vacancies.on("input", function () {
        updateFilters(LSCSearchData ? LSCSearchData : dataAll);
    });

    // translation button - toggle language
    $targets.translate.on("click", (e) => {
        lang = lang === "en" ? "es" : "en";
        infoWindow.close();
        translatePage(lang, $targets);
        updateResults();
    });

    // find me button - do a geocode request with the user's browser location
    $targets.findMe.on("click", (e) => {
        //check to see if http or https (only works with https)
        if (top.location.href.split(":")[0] !== "https") {
            const $modal = initModal("Secure Connection Required", $("<p>").html("This feature requires a secure connection."));
            const myModal = new Modal($modal);
            myModal.show();
            return false;
        }

        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(position => {
                geocodeRequest(new google.maps.LatLng(position.coords.latitude, position.coords.longitude), googleGeoCallback);
            }, err => {
                console.warn(`ERROR(${err.code}): ${err.message}`);
                const $modal = initModal("Browser Location Required", $("<p>").html("This feature requires location to be enabled in your browser and OS."));
                const myModal = new Modal($modal);
                myModal.show();
                return false;
            }, {timeout: 4000});
        }
    });

    // reset button
    $targets.reset.on("click", resetMap);

    // collapse main window
    $targets.displayToggle.on("click", (e) => {
        $targets.mainWrapper.toggle({
            duration: 0,
            complete: () => {
                $targets.displayToggle.attr("aria-expanded", (index, attr) => (attr === "true" ? "false" : "true"));

                scrollToRow($("tr.selected-row"), $targets.results);

                // center the map on the infoWindow if there is one, otherwise use the data to set boundaries
                if (infoWindow.getMap()) {
                    map.setCenter(infoWindow.getPosition());
                } else {
                    setMapBounds(map, markers, addressMarker);
                }

                // yank the map into position based on the state of the overlay
                positionMarkersOnMap(map, $targets);
            }
        });
    });

    // resize the results list and move the markers if the window size changes
    $(window).on("resize", () => {
        setMapBounds(map, markers, addressMarker);
        positionMarkersOnMap(map, $targets);
    });

    //hold mouse down for 1.5 seconds to trigger radius search
    new LongPress(1500);
    map.addListener("longPress", event => geocodeRequest(event.latLng, googleGeoCallback));

    /********************* UTILITY FUNCTIONS *********************/

    // the main data filtering function. 
    // Take an incoming dataset (either dataAll or LSCData)
    // adjust the UI according to the incoming data (ex. disable or hide buttons when not applicable to the current dataset)
    // filter the incoming dataset according to the UI and store the result in filteredData for the autocomplete map
    // set the markers using the filtered data
    // draw the data table using the markers
    function updateFilters(data) {
        map.data.forEach(feature => map.data.remove(feature));
        adjustFilterUI(data);
        filteredData = filterData(data);
        setMarkers(filteredData);
        updateResults();

        function filterData(data) {
            // start with all data if no filters are selected
            let stage1 = [...data];

            if ($targets.lscType.val() !== "all") {
                stage1 = data.filter(d => {
                    if (d.vacancies) {
                        return (d.vacancies.LSCType === $targets.lscType.val());
                    }
                    return false;
                });
            }

            let stage2 = [...stage1];

            if ($targets.seatType.val() !== "all") {
                switch ($targets.seatType.val()) {
                    case "parent":
                        stage2 = stage1.filter(d => d.vacancies.PARENT_MAX > 0);
                        break;
                    case "community":
                        stage2 = stage1.filter(d => d.vacancies.COMMUNITY_MAX > 0);
                        break;
                    case "teacher":
                        stage2 = stage1.filter(d => d.vacancies.TEACHER_MAX > 0);
                        break;
                    case "student":
                        stage2 = stage1.filter(d => d.vacancies.STUDENT_MAX > 0);
                        break;
                    case "nonteacher":
                        stage2 = stage1.filter(d => d.vacancies.NONTCHSTAFF_MAX > 0);
                        break;
                    case "principal":
                        stage2 = stage1.filter(d => d.vacancies.PRINCIPAL_MAX > 0);
                        break;
                    case "specialized":
                        stage2 = stage1.filter(d => specializedRolesCount(d.vacancies).max > 0);
                        break;
                }
            }

            adjustFilterUI(stage2);

            let stage3 = [...stage2];

            // check to see if any filter buttons are active
            if ($("input:checked", $targets.filters).length > 0) {
                let full = [];
                let functioning = [];
                let uq = [];
                let nolsc = [];
                let inactive = [];
                $("input:checked", $targets.filters).each(function () {
                    switch ($(this).attr(("id"))) {
                        case "filter-full":
                            full = stage2.filter(d => vacancyType(d.vacancies) === "full");
                            break;
                        case "filter-functioning":
                            functioning = stage2.filter(d => vacancyType(d.vacancies) === "functioning");
                            break;
                        case "filter-under-quorum":
                            uq = stage2.filter(d => vacancyType(d.vacancies) === "under-quorum");
                            break;
                        case "filter-no-lsc":
                            nolsc = stage2.filter(d => vacancyType(d.vacancies) === "no-lsc");
                            break;
                        case "filter-inactive":
                            inactive = stage2.filter(d => vacancyType(d.vacancies) === "inactive");
                            break;
                    }
                });
                stage3 = [...full, ...functioning, ...uq, ...nolsc, ...inactive];
            }

            return stage3;
        }

        // enable/disable filters based on incoming data
        function adjustFilterUI(data) {
            // hide all filters to start. Then show which ones are relevant to the current dataset
            $(".form-check", $targets.filters).attr("hidden", "hidden");

            if (data.find(s => vacancyType(s.vacancies) === "full")) {
                $(".form-check:has(#filter-full)", $targets.filters).removeAttr("hidden");
            }
            if (data.find(s => vacancyType(s.vacancies) === "functioning")) {
                $(".form-check:has(#filter-functioning)", $targets.filters).removeAttr("hidden");
            }
            if (data.find(s => vacancyType(s.vacancies) === "under-quorum")) {
                $(".form-check:has(#filter-under-quorum)", $targets.filters).removeAttr("hidden");
            }
            if (data.find(s => vacancyType(s.vacancies) === "inactive")) {
                $(".form-check:has(#filter-inactive)", $targets.filters).removeAttr("hidden");
            }
        }
    }

    // create the option elements for the LSC type menu
    function buildMenu() {
        const types = [...new Set(vacancyData.map(item => item.LSCType))].sort();
        types.forEach(t => {
            if (/\S/.test(t)) { // remove whitespace entries
                $targets.lscType.append($("<option>").val(t).html(t));
            }
        })
    }


    // the main logic to determine vacancy status. 
    // In this case any schools 0% vacant are full, schools < 50% vacant are functioning, and schools >= 50% vacant are considered under-quorum.
    // also includes an override type of inactive, which is a manually set type
    function vacancyType(v) {
        if (v) {
            if (v.LSCType === "Inactive") {
                return "inactive";
            } else if (v.PctVacant === 0) {
                return "full";
            } else if (v.PctVacant >= 50) {
                return "under-quorum"
            } else {
                return "functioning";
            }
        }
        return "no-lsc";
    }

    // set the array of map markers based on an incoming set of features
    // this array becomes the master source of data for the script
    // each marker contains an advancedMarker element that references the google advancedMarker object that draws the pin
    // and a program field that contains all the data for the program
    function setMarkers(data) {
        // clear out any existing markers
        markers.forEach(m => m.map = null);
        markers.length = 0;

        data.forEach(f => {
            const vacancyInfo = f.vacancies;
            const type = vacancyType(f.vacancies);
            // find the next meeting date and attach it to the vacancy info object
            if (meetingData) {
                f.vacancies.nextMeeting = nextMeetingDate(f.vacancies.SchoolId.toString());

                // find the next date in the meeting data for a given school
                function nextMeetingDate(id) {
                    const now = new Date();
                    const meetingDates = parsedMeetingData(schoolMeetingData(id));
                    if (meetingDates) {
                        meetingDates.sort((a, b) => a.getTime() - b.getTime());
                        return meetingDates.find(d => d.getTime() > now.getTime());
                    }

                    // search the meetingData array and return records matching a given school id
                    function schoolMeetingData(id) {
                        return (meetingData.filter((e) => e.SchoolId.toString() === id));
                    }

                    // parse the incoming meeting data into an array of dates
                    function parsedMeetingData(data) {
                        const meetings = [];
                        for (let i = 0; i < 24; ++i) {
                            const keyNumber = i + 1;
                            const key = `Meeting${keyNumber.toString().padStart(2, "0")}`;
                            if (data[0][key]) {
                                const d = new Date(data[0][key]);
                                if (isValidDate(d)) {
                                    meetings[i] = d;
                                }
                            }
                        }
                        return meetings;

                        function isValidDate(d) {
                            return d instanceof Date && !isNaN(d);
                        }
                    }
                }
            }

            // get the members for the selected school and attach them to the vacancy info object
            const members = memberData.filter((member) => member.SchoolId.toString() === f.properties.School_ID.toString());
            if (members) {
                members.sort((a, b) => a.MemberName.localeCompare(b.MemberName));
                members.sort((a, b) => a.LSCMemberType.localeCompare(b.LSCMemberType));
                f.vacancies.members = members;
            }
            const pinStyle = schoolStyle(type);
            pinStyle.scale = .7;

            pinStyle.glyphText = vacancyNumber(f.vacancies);

            const advancedMarker = new AdvancedMarkerElement({
                map: map,
                position: {
                    lng: f.geometry.coordinates[0],
                    lat: f.geometry.coordinates[1]
                },
                title: f.properties.Name,
            });
            advancedMarker.append(new PinElement(pinStyle));

            // add event listener for the marker information popup
            advancedMarker.addListener("gmp-click", () => {
                // open the info window for a given marker
                openInfoWindow(advancedMarker);

                // set the highlight and scroll to the selected row
                $(`.resultRow_${toSnakeCase(f.properties.Name)}`).addClass("selected-row");
                scrollToRow($("tr.selected-row"), $targets.results);

                // set the input field to match the selection
                $targets.input.val(advancedMarker.properties.Name);
            });


            advancedMarker.properties = f.properties;
            advancedMarker.vacancyInfo = vacancyInfo;
            advancedMarker.boundary = f.boundary;
            advancedMarker.labelLocation = f.labelLocation;
            advancedMarker.type = type;

            // add this marker to the array of all markers
            markers.push(advancedMarker);

            // return the number of vacancies for a given seat type. This is calculated from subtracting the filled seats from the max allowed
            function vacancyNumber(v) {
                switch ($targets.vacancies.val()) {
                    case "total":
                        return (totalCount(v).max > 0 ? String(totalCount(v).max - totalCount(v).mem) : "");
                    case "parent":
                        return (v.PARENT_MAX > 0 ? String(v.PARENT_MAX - v.PARENT_MEM) : "");
                    case "community":
                        return (v.COMMUNITY_MAX > 0 ? String(v.COMMUNITY_MAX - v.COMMUNITY_MEM) : "");
                    case "teacher":
                        return (v.TEACHER_MAX > 0 ? String(v.TEACHER_MAX - v.TEACHER_MEM) : "");
                    case "student":
                        return (v.STUDENT_MAX > 0 ? String(v.STUDENT_MAX - v.STUDENT_MEM) : "");
                    case "nonteacher":
                        return (v.NONTCHSTAFF_MAX > 0 ? String(v.NONTCHSTAFF_MAX - v.NONTCHSTAFF_MEM) : "");
                    case "principal":
                        return (v.PRINCIPAL_MAX > 0 ? String(v.PRINCIPAL_MAX - v.PRINCIPAL_MEM) : "");
                    case "specialized":
                        return (specializedRolesCount(v).max > 0 ? String(specializedRolesCount(v).max - specializedRolesCount(v).mem) : "");
                }
                return "";
            }

            // style the pins based on type
            function schoolStyle(type) {
                if (type === "full") {
                    return {
                        background: color.green,
                        borderColor: color.darkGreen,
                        glyphText: "",
                        glyphColor: textContrast(color.green)
                    };
                } else if (type === "under-quorum") {
                    return {
                        background: color.yellow,
                        borderColor: color.darkYellow,
                        glyphText: "",
                        glyphColor: textContrast(color.yellow)
                    };
                } else if (type === "functioning") {
                    return {
                        background: "#047a6e",
                        borderColor: "#00463F",
                        glyphText: "",
                        glyphColor: textContrast("#047a6e")
                    };
                } else if (type === "inactive") {
                    return {
                        background: "#8B2BA1",
                        borderColor: "#621E71",
                        glyphText: "",
                        glyphColor: textContrast("#8B2BA1")
                    };
                } else { // na
                    return {
                        background: color.grey,
                        borderColor: color.dark,
                        glyphText: ""
                    };
                }
            }
        });
    }

    // fill in the results table using the data contained in the global markers[] array and perform UI adjustments
    function updateResults() {
        infoWindow.close();
        setMapBounds(map, markers, addressMarker);
        positionMarkersOnMap(map, $targets);

        // put the results table into the active tab
        $targets.results.html($resultTable($tableHead(), $tableBody(markers)));

        if (dataTable) {
            dataTable.destroy();
        }
        dataTable = new DataTable(".resultList", {
            paging: false,
            searching: false,
            info: false,
            order: [[0, "asc"]],
            drawCallback: function (settings) {
                setMapBounds(map, markers, addressMarker);
                positionMarkersOnMap(map, $targets);
            },
            columnDefs: [
                {
                    targets: [1, 12],
                    className: "dt-head-left"
                }
            ]
        });
        $(".dt-info").html("");

        // return the results table header
        function $tableHead() {
            // create the table header row
            const $tr = $("<tr>").addClass("results-header");
            const $schoolCell = $("<th>").html(`${translations[lang]["label-schools"]}: ${markers.length}`);
            $tr.html($schoolCell);
            // TODO - replace loop with translation labeled rows like meeting
            const headers = [
                `%&nbsp;${translations[lang]["label-full"]}`,
                translations[lang]["label-school-type"],
                translations[lang]["label-total"],
                translations[lang]["label-parent"],
                translations[lang]["label-community"],
                translations[lang]["label-teacher"],
                translations[lang]["label-student"],
                translations[lang]["label-non-teacher-staff"],
                translations[lang]["label-principal"],
                translations[lang]["label-specialized-roles"],
                translations[lang]["label-lsc-type"]
            ];
            headers.forEach(h => {
                $tr.append($("<th>").html(h));
            });
            const $meetingCell = $("<th>").html(translations[lang]["label-nextmeeting"]);
            $tr.append($meetingCell);
            return $("<thead>").html($tr);
        }

        // return results table body rows
        function $tableBody(markers) {
            const $out = $("<tbody>");

            // make a row for each marker
            markers.forEach(m => {
                $out.append($resultRow(m));
            });

            return ($out);

            function $resultRow(m) {
                const v = m.vacancyInfo;
                const p = m.properties;

                // CREATE ROW
                const $row = $("<tr>").addClass(`resultRow_${toSnakeCase(p.Name)} type_${m.type}`);

                // CREATE CELLS
                const $schoolLink = $("<button>").addClass("d-flex btn btn-link w-100 text-start");
                $schoolLink.html($("<div>").addClass("marker-icon").html(markerSvg));
                $schoolLink.append($("<div>").html(p.Name));

                const $cellName = $("<td>").addClass(`bcq-p-1 bcq-ps-3`).html($schoolLink);

                // starting with blank text for the handful of schools without vvacancy data
                let pctFullText = "";
                let schoolTypeText = "";
                let TotalText = "";
                let LSCTypeText = "";
                let parentText = "";
                let communityText = "";
                let teacherText = "";
                let studentText = "";
                let nonTeacherStaffText = "";
                let principalText = "";
                let specializedRolesText = "";
                let nextMeetingText = "";
                let sort = 0;


                // fill in the vacancy data text for the columns
                if (v) {
                    const full = 100 - v.PctVacant;
                    pctFullText = `${+full.toFixed(2)}%`;
                    schoolTypeText = v.SchoolType;
                    LSCTypeText = v.LSCType;
                    TotalText = tallyText(totalCount(v).mem, totalCount(v).max);
                    parentText = v.PARENT_MAX > 0 ? tallyText(v.PARENT_MEM, v.PARENT_MAX) : "";
                    communityText = v.COMMUNITY_MAX > 0 ? tallyText(v.COMMUNITY_MEM, v.COMMUNITY_MAX) : "";
                    teacherText = v.TEACHER_MAX > 0 ? tallyText(v.TEACHER_MEM, v.TEACHER_MAX) : "";
                    studentText = v.STUDENT_MAX > 0 ? tallyText(v.STUDENT_MEM, v.STUDENT_MAX) : "";
                    nonTeacherStaffText = v.NONTCHSTAFF_MAX > 0 ? tallyText(v.NONTCHSTAFF_MEM, v.NONTCHSTAFF_MAX) : "";
                    principalText = v.PRINCIPAL_MAX > 0 ? tallyText(v.PRINCIPAL_MEM, v.PRINCIPAL_MAX) : "";
                    specializedRolesText = specializedRolesCount(v).max > 0 ? tallyText(specializedRolesCount(v).mem, specializedRolesCount(v).max) : "";
                    nextMeetingText = meetingText(v.nextMeeting);
                    if (v.nextMeeting) {
                        sort = v.nextMeeting.getTime();
                    }
                }

                const $cellMeeting = $cell(nextMeetingText);
                $cellMeeting.attr("data-order", sort).addClass("bcq-pe-3");

                // POPULATE ROW
                $row.append(
                    $cellName,
                    $cell(pctFullText),
                    $cell(schoolTypeText),
                    $cell(TotalText),
                    $cell(parentText),
                    $cell(communityText),
                    $cell(teacherText),
                    $cell(studentText),
                    $cell(nonTeacherStaffText),
                    $cell(principalText),
                    $cell(specializedRolesText),
                    $cell(LSCTypeText),
                    $cellMeeting);

                // ADD EVENTS
                $schoolLink.on("click", () => {
                    openInfoWindow(m);
                    $targets.input.val(m.properties.Name);
                    $row.addClass("selected-row");
                });
                $row.on("mouseenter", () => animateMarker(m));
                $row.on("mouseleave", () => stopAnimateMarker(m));

                return $row;

                function $cell(t) {
                    return $("<td>").addClass("bcq-p-1").html(t);
                }

                // return a formatted date string or error message
                function meetingText(d) {
                    if (meetingData) {
                        if (d) {
                            return formattedDate(d);
                        }
                    } else {
                        return translations[lang]["label-datanotavailable"];
                    }
                }
            }
        }
    }

    function totalCount(v) {
        return {
            mem: (v.PARENT_MEM +
                v.COMMUNITY_MEM +
                v.TEACHER_MEM +
                v.STUDENT_MEM +
                v.NONTCHSTAFF_MEM +
                v.PRINCIPAL_MEM +
                specializedRolesCount(v).mem),
            max: (v.PARENT_MAX +
                v.COMMUNITY_MAX +
                v.TEACHER_MAX +
                v.STUDENT_MAX +
                v.NONTCHSTAFF_MAX +
                v.PRINCIPAL_MAX +
                specializedRolesCount(v).max)
        }
    }


    function specializedRolesCount(v) {
        return ({
            mem: (v.ADVOCATE_MEM +
                v.BUSINESS_MEM +
                v.CADET_CMDR_MEM +
                v.CHIEF_EDU_OFF_MEM +
                v.COMMANDANT_MEM +
                v.ED_EXPERT_MEM +
                v.JROTC_MEM),
            max: (v.ADVOCATE_MAX +
                v.BUSINESS_MAX +
                v.CADET_CMDR_MAX +
                v.CHIEF_EDU_OFF_MAX +
                v.COMMANDANT_MAX +
                v.ED_EXPERT_MAX +
                v.JROTC_MAX)
        });
    }

    // print a date in local Chicago time
    function formattedDate(d) {
        return d.toLocaleString("en-US", {
            timeZone: "America/Chicago",
            dateStyle: "short",
            timeStyle: "short"
        });
    }

    function tallyText(X, Y) {
        return `${X} ${translations[lang]["label-of"]} ${Y}`;
    }

    // populates the info window
    function openInfoWindow(marker) {
        // remove any polygons
        map.data.forEach(feature => map.data.remove(feature));
        // clear any existing label markers
        labelMarkers.forEach(l => l.setMap(null));
        labelMarkers.length = 0;

        currentMarker = marker;
        const position = marker.position;

        // have the info window appear in the center of the circles and offset higher on icons.
        infoWindow.setOptions({
            pixelOffset: new google.maps.Size(0, -26),
            maxWidth: 600,
        });
        infoWindow.setHeaderContent(infoWindowHeaderContent(marker));
        infoWindow.setContent(infoWindowContent(marker));
        infoWindow.setPosition(position);
        infoWindow.open(map);
        // remove the selection highlight from the "X" in the newly opened window and any other links
        infoWindow.addListener("visible", () => {
            $(".gm-style-iw-chr button[title=Close]").trigger("blur");
            $(".infowindow-content a").trigger("blur");
        });
        
        const polygon = showPolygon(map, marker.boundary, POLYGON_STYLE(), marker.properties.Name);
        if (polygon) {
            labelMarkers.push(polygon.labelMarker);
        }
        setMapBounds(map, marker.boundary);
        positionMarkersOnMap(map, $targets);

        function infoWindowHeaderContent(m) {
            const p = m.properties;

            const $content = $("<div>").addClass("infowindow-content mb-2");
            const $header = $("<h4>").html(p.Name).addClass("mb-1");
            const $schoolType = $("<div>").html(p.Type);
            const $address = $("<div>").html($addressDirectionLink(p.Address, addressMarker));
            const $phone = $("<div>").html($("<a>").addClass("text-color").attr({"href": `tel:${p.Phone}`}).html(p.Phone));
            $content.append($header, $schoolType, $address, $phone);
            return $content[0];
        }

        function infoWindowContent(m) {
            const p = m.properties;
            const v = m.vacancyInfo;
            let members;
            let nextMeetingText = "";
            if (m.vacancyInfo) {
                nextMeetingText = meetingText(v.nextMeeting);
                members = v.members;
            }

            const $content = $("<div>").addClass("infowindow-content");
            if (v) {
                const full = 100 - v.PctVacant;
                $content.append($("<div>").html($("<strong>").html(`${full.toFixed(2)}% Full`)).addClass("vacancy-info"));

                if (v.PARENT_MAX > 0) {
                    const $parentMembers = $("<div>");
                    const $parentMemberStats = $("<strong>").html(tallyText(v.PARENT_MEM, v.PARENT_MAX));
                    $parentMembers.html(`${translations[lang]["label-pmembers"]}: `).append($parentMemberStats);
                    $content.append($parentMembers);
                }
                if (v.COMMUNITY_MAX > 0) {
                    const $communityMembers = $("<div>");
                    const $communityMembersStats = $("<strong>").html(tallyText(v.COMMUNITY_MEM, v.COMMUNITY_MAX));
                    $communityMembers.html(`${translations[lang]["label-cmembers"]}: `).append($communityMembersStats);
                    $content.append($communityMembers);
                }
                //TODO - Translate labels
                if (v.TEACHER_MAX > 0) {
                    $content.append($("<div>").html(`Teachers: `).append($("<strong>").html(tallyText(v.TEACHER_MEM, v.TEACHER_MAX))));
                }
                if (v.STUDENT_MAX > 0) {
                    $content.append($("<div>").html(`Students: `).append($("<strong>").html(tallyText(v.STUDENT_MEM, v.STUDENT_MAX))));
                }
                if (v.NONTCHSTAFF_MAX > 0) {
                    $content.append($("<div>").html(`Non-Teacher Staff: `).append($("<strong>").html(tallyText(v.NONTCHSTAFF_MEM, v.NONTCHSTAFF_MAX))));
                }
                if (v.PRINCIPAL_MAX > 0) {
                    $content.append($("<div>").html(`Principals: `).append($("<strong>").html(tallyText(v.PRINCIPAL_MEM, v.PRINCIPAL_MAX))));
                }
                if (v.ADVOCATE_MAX > 0) {
                    $content.append($("<div>").html(`Advocate: `).append($("<strong>").html(tallyText(v.ADVOCATE_MEM, v.ADVOCATE_MAX))));
                }
                if (v.BUSINESS_MAX > 0) {
                    $content.append($("<div>").html(`Business: `).append($("<strong>").html(tallyText(v.BUSINESS_MEM, v.BUSINESS_MAX))));
                }
                if (v.CADET_CMDR_MAX > 0) {
                    $content.append($("<div>").html(`Cadet Commander: `).append($("<strong>").html(tallyText(v.CADET_CMDR_MEM, v.CADET_CMDR_MAX))));
                }
                if (v.CHIEF_EDU_OFF_MAX > 0) {
                    $content.append($("<div>").html(`Chief Education Officer: `).append($("<strong>").html(tallyText(v.CHIEF_EDU_OFF_MEM, v.CHIEF_EDU_OFF_MAX))));
                }
                if (v.COMMANDANT_MAX > 0) {
                    $content.append($("<div>").html(`Commandant: `).append($("<strong>").html(tallyText(v.COMMANDANT_MEM, v.COMMANDANT_MAX))));
                }
                if (v.ED_EXPERT_MAX > 0) {
                    $content.append($("<div>").html(`Education Expert: `).append($("<strong>").html(tallyText(v.ED_EXPERT_MEM, v.ED_EXPERT_MAX))));
                }
                if (v.JROTC_MAX > 0) {
                    $content.append($("<div>").html(`JROTC Instructor: `).append($("<strong>").html(tallyText(v.JROTC_MEM, v.JROTC_MAX))));
                }

                // display next meeting info if it exists
                if (nextMeetingText) {
                    const $nextMeeting = $("<div>").html(`${translations[lang]["label-nextmeeting"]}: ${nextMeetingText}`);
                    $content.append($nextMeeting);
                }

                if (members) {
                    const $table = $("<table>").addClass("table table-sm table-striped mb-0 mt-2");
                    const $tableHead = $("<thead>");
                    const $tableHeadRow = $("<tr>");
                    $tableHeadRow.html($("<th>").text(translations[lang]["label-type"]));
                    $tableHeadRow.append($("<th>").text(translations[lang]["label-name"]));

                    const $tableBody = $("<tbody>");
                    members.forEach(vote => {
                        const $tableRow = $("<tr>");
                        $tableRow.html($("<td>").html(vote.LSCMemberType));
                        $tableRow.append($("<td>").html(vote.MemberName));
                        $tableBody.append($tableRow);
                    });

                    $tableHead.html($tableHeadRow);
                    $table.append($tableHead, $tableBody);
                    $content.append($table);
                }
            }
            return $content[0]

            // return a formatted date string or error message
            function meetingText(d) {
                if (meetingData) {
                    if (d) {
                        return formattedDate(d);
                    }
                } else {
                    return translations[lang]["label-datanotavailable"];
                }
            }
        }
    }

    // perform a search on the markers from the general input field - does 
    function mixedSearch(input) {
        $targets.input.trigger("blur");

        if (input) {
            const properties = filteredData.map(d => d.properties);
            const match = inputSubstringMatch(input, properties, "Name");
            if (match) {
                $targets.input.val(match.Name);
                selectSchool(markers.find(m => m.properties.Name === match.Name));
            } else {
                geocodeRequest($targets.input.val(), googleGeoCallback);
            }
        } else {
            const myModal = new Modal(initModal("Enter Address or School", $("<p>").html(`Please enter an address or school`)));
            myModal.show();
        }
    }

    // open an infowindow for a marker and scroll to its row in the table
    function selectSchool(m) {
        // opening the infowindow first because the "selected-row" class is cleared when old windows are closed
        openInfoWindow(m);

        // add the class "selected-row" for targeting and highlighting
        $(`.resultRow_${toSnakeCase(m.properties.Name)}`).addClass("selected-row");
        scrollToRow($("tr.selected-row"), $targets.results);
        $targets.input.val(m.properties.Name);
        $targets.input.trigger("blur");
    }

    // reset all map elements to their initial states
    function resetMap() {
        clearMapElements(); //clear markers

        // reset search
        $targets.input.val("");
        location = null;

        // remove the quicksort
        if (dataTable) {
            dataTable.destroy();
        }

        // reset the filter buttons
        $(".active", $targets.filters).removeClass("active");

        // reset the menus
        $targets.seatType.val("all");
        $targets.lscType.val("all");
        $targets.vacancies.val("total");

        // reset the search radius
        $targets.radius.val("2");
        $targets.radius.attr({"hidden": "hidden"});

        // reset the filteredData array to the full dataset using updateFilters(dataAll) to reset filter UI state
        updateFilters(dataAll);

        // reset any address search
        LSCSearchData = null;

        // reposition map
        setMapBounds(map, markers, addressMarker);
        positionMarkersOnMap(map, $targets);
    }

    /******************** MAP FUNCTIONS ********************/

    // take the returned address, format it, put it in the address field, do a geojson search of cps data with a radius restriction, and display the results
    function googleGeoCallback(results, status, request) {
        $targets.input.trigger("blur");

        // reset any existing filters - prevents case where all data could be filtered out
        $(".active", $targets.filters).removeClass("active");

        // geocoder status ok
        if (status === google.maps.GeocoderStatus.OK) {
            if (results[0]) {
                if (results[0].formatted_address !== "Chicago, IL, USA") {
                    clearMapElements();

                    const result = results[0];
                    const formattedAddress = results[0].formatted_address.slice(0, -5); // remove ", USA" from returned address

                    $targets.input.val(formattedAddress);
                    addressMarker = setAddressMarker(map, result.geometry.location);
                    addressMarker.formattedAddress = formattedAddress;
                    // $targets.radius.removeAttr("hidden");
                    location = result.geometry.location;
                    // show the address marker and pan to it in case it is out of bounds and we stop here
                    map.panTo(location);

                    LSCSearch(location);

                    updateFilters(LSCSearchData);

                    // show intersecting polygons for a geocode location
                    async function LSCSearch(location) {
                        // clearMapElements();

                        // see if the location is contained within any of the feature polygons
                        const foundFeatures = findFeatures(location);
                        if (foundFeatures.length > 0) {
                            LSCSearchData = [];
                            foundFeatures.forEach(f => {
                                LSCSearchData.push(schoolData.features.find(s => s.properties.School_ID === f.properties.SCHOOL_ID));
                            });
                            // positionMarkersOnMap(map, $targets);
                        } else {
                            LSCSearchData = [];
                            const $modal = initModal("Out of Bounds", $("<p>").html("Address is out of bounds"));
                            const myModal = new Modal($modal);
                            myModal.show();
                        }

                        // look through the dataset and return the feature polygon that contains the location
                        function findFeatures(location) {
                            const found = [];
                            boundaryData.features.forEach(f => {
                                if (f.geometry.type === "Polygon") {
                                    if (checkPolygon(location, f.geometry.coordinates)) {
                                        found.push(f);
                                    }
                                } else if (f.geometry.type === "MultiPolygon") {
                                    f.geometry.coordinates.forEach(m => {
                                        if (checkPolygon(location, m)) {
                                            found.push(f);
                                        }
                                    });
                                }
                            });
                            return found;

                            function checkPolygon(location, coordinates) {
                                // incoming coordinates are an array of arrays of [longitude, latitude] pairs. Convert them to an array of arrays google LatLng objects
                                // if the top level array has one list, it is a simple polygon. Additional lists of coordinates are holes in the main polygon
                                // we are creating a maps.Polygon from coordinates rather than a map.data.addGeoJson object from the feature in order to use maps.geometry and containsLocation
                                const latLngCoordinates = [];
                                coordinates.forEach(p => {
                                    const pList = [];
                                    p.forEach(c => pList.push(new google.maps.LatLng(c[1], c[0])));
                                    latLngCoordinates.push(pList);
                                });

                                // create a map polygon with the latLngCoordinates to check against the location
                                const testPolygon = new Polygon({paths: latLngCoordinates});
                                const test = google.maps.geometry.poly.containsLocation(location, testPolygon);
                                testPolygon.setMap(null);
                                return test;
                            }
                        }
                    }
                }

            } else {
                $targets.input.val("");
                const myModal = new Modal(initModal("Enter a Chicago Address", $("<p>").html(`Please enter a valid Chicago address`)));
                myModal.show();
            }
        }
        // geocoder status not ok
        else {
            let $modal;
            if (request.location) { // if we did a lat/long search
                $modal = initModal("Geocoder failed", $("<p>").html(`Geocoder failed due to: ${status}`));
            } else { // if we did an address search
                $modal = initModal("Geocoder failed", $("<p>").html(`We could not find your address: ${status}`));
            }
            const myModal = new Modal($modal);
            myModal.show();
        }
    }

    // clear out elements displayed on the map
    function clearMapElements() {
        // close any open info windows
        infoWindow.close();

        // Reset the markers array if it already contains data
        markers.forEach(m => m.map = null);
        markers.length = 0;

        if (addressMarker) {
            addressMarker.map = null;
        }

        // clear the results list
        $targets.results.html("");
    }

    // LongPress on the map sets up mouse events and custom longPress event
    // thanks to stackoverflow - Leiko
    // https://stackoverflow.com/questions/6264853/using-longclick-taphold-with-google-maps-in-jquery-mobile
    function LongPress(length) {
        this.length_ = length;
        this.map_ = map;
        this.timeoutId_ = null;
        map.addListener("mousedown", e => this.onMouseDown_(e));
        map.addListener("mouseup", e => this.onMouseUp_(e));
        map.addListener("drag", e => this.onMapDrag_(e));
    }

    LongPress.prototype.onMouseUp_ = function (e) {
        clearTimeout(this.timeoutId_);
    };

    LongPress.prototype.onMouseDown_ = function (e) {
        clearTimeout(this.timeoutId_);
        const map = this.map_;
        this.timeoutId_ = setTimeout(() => google.maps.event.trigger(map, "longPress", e), this.length_);
    };

    LongPress.prototype.onMapDrag_ = function (e) {
        clearTimeout(this.timeoutId_);
    };
} catch (e) {
    console.error(e);
}
